'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import {
  Dialog as RACDialog,
  Heading as RACHeading,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { FocusableContainer } from '../internal/focusable-container/focusable-container.js';
import { XButton } from '../internal/x-button/x-button.js';
import type { AriaLabelingProps, CommonProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface ModalDialogRenderProps {
  /** Closes the modal dialog when called. */
  close: () => void;
}

export interface ModalDialogProps extends CommonProps, AriaLabelingProps {
  /** The contents of the modal dialog. A function may be provided to access a function to close the modal dialog. */
  children: ReactNode | ((renderProps: ModalDialogRenderProps) => ReactNode);
  /** The title of the modal dialog. */
  title: string;
  /**
   * The size of the modal dialog.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether the modal dialog can be closed by clicking the close button, clicking outside of the modal dialog, or pressing the Escape key.
   * When `false`, the modal dialog can only be closed programmatically.
   * @default true
   */
  isDismissible?: boolean;
  /**
   * Whether the modal dialog is open (controlled).
   * If using `DialogRoot`, this prop has no effect - provide `isOpen` to `DialogRoot` instead.
   */
  isOpen?: boolean;
  /**
   * Whether the modal dialog is open by default (uncontrolled).
   * If using `DialogRoot`, this prop has no effect - provide `defaultOpen` to `DialogRoot` instead.
   */
  defaultOpen?: boolean;
  /**
   * Handler that is called when the modal dialog's open state changes.
   * If using `DialogRoot`, this prop has no effect - provide `onOpenChange` to `DialogRoot` instead.
   */
  onOpenChange?: (isOpen: boolean) => void;
}

function ModalDialog(
  {
    children,
    title,
    size = 'medium',
    UNSAFE_className,
    UNSAFE_style,
    isDismissible = true,
    isOpen,
    defaultOpen,
    onOpenChange,
    ...props
  }: ModalDialogProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!title && !ariaLabel && !ariaLabelledBy) {
      console.warn('ModalDialog requires one of title / aria-label / aria-labelledby for accessibility');
    }
  }, [title, ariaLabel, ariaLabelledBy]);

  const messages = useLocalizedMessages('common');

  return (
    <RACModalOverlay
      className="cim-modal-overlay"
      isDismissable={isDismissible}
      isKeyboardDismissDisabled={!isDismissible}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      data-cim-style-root
    >
      <RACModal className={clsx('cim-modal', UNSAFE_className)} style={UNSAFE_style} data-size={size}>
        <RACDialog {...props} ref={ref} className="cim-modal-dialog">
          {(renderProps) => (
            <>
              <div className="cim-modal-header">
                {title && (
                  <RACHeading
                    slot="title"
                    className={clsx('cim-modal-title', textStyle({ variant: 'title-5', tone: 'base' }))}
                  >
                    {title}
                  </RACHeading>
                )}
                {isDismissible && <XButton aria-label={messages.format('dismiss')} slot="close" />}
              </div>
              {typeof children === 'function' ? children(renderProps) : children}
            </>
          )}
        </RACDialog>
      </RACModal>
    </RACModalOverlay>
  );
}

/**
 * Displays an overlay element which blocks interaction with outside elements.
 *
 * See [modal dialog usage guidelines](https://ui.cimpress.io/components/modal-dialog/).
 */
const _ModalDialog = forwardRef(ModalDialog, 'ModalDialog');

export { _ModalDialog as ModalDialog };

export interface ModalDialogBodyProps {
  /** The content to render within the modal dialog. */
  children: ReactNode;
}

/** Renders content within `ModalDialog`. */
export function ModalDialogBody(props: ModalDialogBodyProps) {
  return <FocusableContainer {...props} className="cim-modal-body" scrollDirection="vertical" />;
}

ModalDialogBody.displayName = 'ModalDialogBody';

export interface ModalDialogActionsProps {
  /** Actions that should be available in the modal dialog. */
  children: ReactNode;
}

/** Renders actions within `ModalDialog`. */
export function ModalDialogActions({ children, ...props }: ModalDialogActionsProps) {
  return (
    <div {...props} className="cim-modal-actions">
      {children}
    </div>
  );
}

ModalDialogActions.displayName = 'ModalDialogActions';
